import React from 'react';
import isEqual from 'react-fast-compare';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {apiClient, getApiUrl} from '../api';
import {FeelLike, LoginResponse, UserDetail} from '../api/types';
import {
  getDebugEmail,
  hydrateForecastPreference,
  selectDashboardConfig,
  selectFavoriteStationIds,
  selectIsLoggedIn,
  selectPurchase,
  selectUserDateTime,
  selectUserFeelLike,
  selectUserLanguage,
  selectUserUnits,
  cleanUpStationList,
  setAmbientWeatherUserLink,
  setBillingStatus,
  setDashboardConfig,
  setDonationEnabled,
  setFeatureFlags,
  setStripeConfig,
  setDavisUserLink,
  setEcowittUserLink,
  setFavoriteStationList,
  setMyAcuriteUserLink,
  setNetatmoEnergyUserLink,
  setNetatmoWeatherUserLink,
  setStationList,
  setStationSort,
  setWeatherflowUserLink,
  setWundergroundUserLink,
  storeDateTime,
  storeFeelLike,
  storeLanguage,
  storeUnits,
  storeUserData,
} from '../store';
import {CommonRootState} from '../store/types';
import {JwtToken} from '../store/user/types';
import {Units} from '../utils';
import {DateTimeConfig} from '../utils/Dates';
import useParamSelector from './useParamSelector';

type Props = {
  deviceLanguage: string;
  uses24HourClock: boolean;
  usesMetricSystem: boolean;
};

const useUserData = ({
  deviceLanguage,
  uses24HourClock,
  usesMetricSystem,
}: Props) => {
  const dispatch = useDispatch();
  const debugEmail = useSelector(getDebugEmail);

  // The login-merge below needs whatever the user set while anonymous, read at
  // the moment it runs. Two refs used to carry that, written during render;
  // the store is the thing they were mirroring, and `useStore` hands out an
  // instance that never changes identity. This is what keeps `editUserDetails`
  // stable -- UserSyncProvider lists it in an effect's dependencies, so
  // re-creating it on every dashboard edit would re-run that fetch -- and
  // subscribing to neither value means an edit no longer re-renders the
  // provider at all.
  const store = useStore<CommonRootState>();

  const editUserDetails = React.useCallback(
    (data: UserDetail, token?: JwtToken) => {
      const {
        email,
        first_name: firstName,
        last_name: lastName,
        profile: {
          units,
          language,
          feel_like: feelLike,
          date_time,
          station_ids,
          forecast_model_order: forecastModelOrder,
          forecast_model_pins: forecastModelPins,
          forecast_model_mode: forecastModelMode,
          forecast_model_strategy: forecastModelStrategy,
          dashboard_config: dashboardConfig,
        },
        is_superuser: isAdmin,
        billing,
        netatmo_energy: netatmoEnergy,
        linked_accounts: linkedAccounts,
        stations,
      } = data;

      // update user info
      dispatch(
        storeUserData({
          token,
          userInfo: {
            email,
            firstName,
            lastName,
            isAdmin: debugEmail ? true : isAdmin,
          },
        }),
      );

      // Store user preferences
      dispatch(storeUnits(units));
      dispatch(storeFeelLike(feelLike));
      dispatch(storeLanguage(language));
      dispatch(storeDateTime(date_time));
      // Forecast model preference (#186: pins/mode/strategy, plus the legacy
      // flat order the backend keeps as a mirror). Unconditional: the reducer
      // applies the server's value only to fields with no pending local edit,
      // so this one call is both the cross-device pull (#178) and the login
      // merge. An anonymous drag-sort survives the login that follows it and
      // is pushed by useForecastPreferenceSync once the token is in Redux; a
      // value this device has no claim on always yields to the server's.
      //
      // The merge used to be gated on "the server sent an empty order", which
      // cannot tell a fresh anonymous edit from a copy left behind by an
      // earlier session of this account. A device offline through a change
      // made elsewhere pushed its stale list back up at its next login, and
      // logging into a second account inherited the first one's order. Only
      // the backend populating the legacy order as a mirror of pins/mode kept
      // that from firing in practice -- this is what removes the need for it.
      dispatch(
        hydrateForecastPreference({
          order: forecastModelOrder ?? null,
          pins: forecastModelPins ?? null,
          mode: forecastModelMode ?? null,
          strategy: forecastModelStrategy ?? null,
        }),
      );
      // Per-station dashboard config: on login, if the server has no config but
      // the user customized locally while anonymous, push the local config up;
      // otherwise the server map wins. "has config" = an object with at least
      // one key. This is still the "the server sent nothing" heuristic the
      // forecast preference above has just moved off, and it is wrong here for
      // the same reason -- but it bites far less hard, because the config is
      // per-station and the backend merges it per key, so a stale push cannot
      // clear a station another device configured. Worth converging on the
      // pending-field model, in its own change.
      const serverHasConfig =
        !!dashboardConfig && Object.keys(dashboardConfig).length > 0;
      const localConfig = selectDashboardConfig(store.getState());
      const localHasConfig =
        !!localConfig && Object.keys(localConfig).length > 0;
      if (token && !serverHasConfig && localHasConfig) {
        // Keep local config + fire-and-forget push it up. Reuse a direct
        // apisauce instance (useApiClient can't run inside this constate body).
        const client = apiClient(getApiUrl());
        client.setHeader('Authorization', `Bearer ${token.accessToken}`);
        client
          .patch('user/profile', {dashboard_config: localConfig})
          .catch(() => {});
      } else {
        dispatch(setDashboardConfig(serverHasConfig ? dashboardConfig : {}));
      }
      if (station_ids && station_ids.length > 0) {
        dispatch(setStationSort(station_ids));
      }

      // update billing status
      dispatch(setBillingStatus(billing));

      // update feature flags
      if (data.feature_flags) {
        dispatch(setFeatureFlags(data.feature_flags));
      }
      dispatch(setStripeConfig(data.stripe_config ?? null));
      dispatch(setDonationEnabled(data.donation_enabled ?? false));

      // update providers
      dispatch(setNetatmoEnergyUserLink(netatmoEnergy));

      // update linked accounts from dictionary (only connected accounts are present)
      if (linkedAccounts) {
        dispatch(setWundergroundUserLink(linkedAccounts.wunderground ?? null));
        dispatch(setDavisUserLink(linkedAccounts.davis ?? null));
        dispatch(setWeatherflowUserLink(linkedAccounts.weatherflow ?? null));
        dispatch(
          setAmbientWeatherUserLink(linkedAccounts.ambient_weather ?? null),
        );
        dispatch(setEcowittUserLink(linkedAccounts.ecowitt ?? null));
        dispatch(setMyAcuriteUserLink(linkedAccounts.my_acurite ?? null));
        dispatch(
          setNetatmoWeatherUserLink(linkedAccounts.netatmo_weather ?? null),
        );
      }

      // Update all weather stations
      dispatch(setStationList(stations));

      // Extract favorite stations
      dispatch(
        setFavoriteStationList(
          stations.filter((station) => station.favorite === true),
        ),
      );

      // Prune any stale entries the persisted state still carries (browsed
      // public stations that aren't favorites or linked). Runs *after* the
      // fresh list + favorites are in place so we never observe an empty
      // stationList — that window is what the iOS widget reads from the App
      // Group, and an empty read leaves Peter/Roy with an empty picker.
      dispatch(cleanUpStationList());
    },
    // `store` is stable for the life of the Provider, so listing it does not
    // cost this callback its identity.
    [debugEmail, dispatch, store],
  );

  const storeLoginDetails = React.useCallback(
    (data: LoginResponse) => {
      const {
        user,
        access_token: accessToken,
        refresh_token: refreshToken,
      } = data;

      editUserDetails(user, {accessToken, refreshToken});
    },
    [editUserDetails],
  );

  // current user language to be used
  const language = useParamSelector(selectUserLanguage, deviceLanguage);

  // Tells whether user is logged in or not
  const isLoggedIn = useSelector(selectIsLoggedIn);

  // Handle User units
  const storedUnits = useSelector(selectUserUnits, isEqual);
  const units = React.useMemo(() => {
    if (storedUnits) {
      return storedUnits;
    } else if (usesMetricSystem) {
      return {
        T: 0,
        RR: 0,
        FF: 0,
        SLP: 0,
        DISTANCE: 0,
      };
    } else {
      return {
        T: 1,
        RR: 1,
        FF: 1,
        SLP: 1,
        DISTANCE: 1,
      };
    }
  }, [storedUnits, usesMetricSystem]) as Units.Units;

  // Handle User DateTime formats
  const storedDateTimeConfig = useSelector(
    selectUserDateTime,
    isEqual,
  ) as DateTimeConfig;
  const dateTimeConfig: DateTimeConfig = React.useMemo(() => {
    if (storedDateTimeConfig) {
      // case where only part of config is present
      if (!Object.keys(storedDateTimeConfig).includes('month_format')) {
        return {
          ...storedDateTimeConfig,
          month_format: uses24HourClock ? 'DD/MM' : 'MM/DD',
        };
      }
      return storedDateTimeConfig;
    } else if (uses24HourClock) {
      // European
      return {
        is_24_hour: true,
        month_format: 'DD/MM',
      };
    }
    // American
    return {
      is_24_hour: false,
      month_format: 'MM/DD',
    };
  }, [storedDateTimeConfig, uses24HourClock]);

  const favoriteIds = useSelector(selectFavoriteStationIds);
  const storedFeelLike = useSelector(selectUserFeelLike);

  const userFeelLike: FeelLike = React.useMemo(() => {
    if (storedFeelLike) {
      return storedFeelLike;
    } else if (usesMetricSystem) {
      return 0;
    }
    return 1;
  }, [storedFeelLike, usesMetricSystem]);

  const purchase = useSelector(selectPurchase);

  const getRegistrationHeaders = React.useCallback(() => {
    const headers = {
      'X-SmartMixin-Units': JSON.stringify(units),
      'X-SmartMixin-FeelLike': JSON.stringify(userFeelLike),
      'X-SmartMixin-DateTime': JSON.stringify(dateTimeConfig),
      'X-SmartMixin-FavoriteIds': JSON.stringify(favoriteIds),
    };

    if (purchase) {
      // Add purchase ID
      headers['X-SmartMixin-PurchaseId'] = purchase.id;
    }
    return headers;
  }, [units, userFeelLike, dateTimeConfig, favoriteIds, purchase]);

  return React.useMemo(
    () => ({
      editUserDetails,
      storeLoginDetails,
      language,
      units,
      userFeelLike,
      dateTimeConfig,
      isLoggedIn,
      getRegistrationHeaders,
    }),
    [
      dateTimeConfig,
      editUserDetails,
      getRegistrationHeaders,
      isLoggedIn,
      language,
      storeLoginDetails,
      units,
      userFeelLike,
    ],
  );
};

export default useUserData;
